/**
 * Input, Select, Textarea — the three text controls.
 *
 * Used by: every form and filter bar in all three apps.
 *
 * They are intentionally dumb: no internal state, no validation, no label. The
 * label/error/aria wiring lives in `FormField`, which is what screens compose
 * with. That split keeps these usable as bare filter inputs in a toolbar where
 * a full field layout would be wrong.
 *
 * Accessibility / touch: 44px minimum height, 16px font on mobile (a smaller
 * font makes iOS Safari zoom on focus, which throws off a POS layout), visible
 * focus ring, and `aria-invalid` styling driven by the attribute rather than a
 * separate prop so FormField controls it.
 */
import { forwardRef } from 'react';
import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes, ReactNode } from 'react';
import { cn, DISABLED, FOCUS_RING } from '../utils.js';

const CONTROL_BASE = cn(
  'w-full rounded-md bg-surface text-fg placeholder:text-fg-subtle',
  'ring-1 ring-inset ring-line-strong',
  'transition-shadow',
  'aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-danger-solid',
  FOCUS_RING,
  DISABLED,
  'disabled:bg-surface-sunken',
);

const CONTROL_SIZE = 'min-h-touch px-3 py-2 text-base sm:text-sm';

/**
 * `prefix` shadows the (deprecated, unused) HTML attribute of the same name,
 * so it is omitted from the base type rather than fought with.
 */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  /** Rendered inside the control on the left (a search glyph, a currency mark). */
  readonly prefix?: ReactNode;
  readonly suffix?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, prefix, suffix, ...rest },
  ref,
) {
  if (prefix === undefined && suffix === undefined) {
    return <input ref={ref} className={cn(CONTROL_BASE, CONTROL_SIZE, className)} {...rest} />;
  }
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md bg-surface ring-1 ring-inset ring-line-strong',
        'focus-within:ring-2 focus-within:ring-ring',
        'px-3',
        className,
      )}
    >
      {prefix !== undefined ? (
        <span className="shrink-0 text-fg-subtle" aria-hidden="true">
          {prefix}
        </span>
      ) : null}
      <input
        ref={ref}
        className={cn(
          'min-h-touch w-full bg-transparent py-2 text-base outline-none placeholder:text-fg-subtle sm:text-sm',
          DISABLED,
        )}
        {...rest}
      />
      {suffix !== undefined ? (
        <span className="shrink-0 text-fg-subtle" aria-hidden="true">
          {suffix}
        </span>
      ) : null}
    </div>
  );
});

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  readonly options: readonly SelectOption[];
  /** Rendered as a disabled first option; use for "-- Chọn --". */
  readonly placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, className, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn(
        CONTROL_BASE,
        CONTROL_SIZE,
        // Native select arrow is kept: it is the affordance users already know,
        // and a custom listbox would need a full keyboard implementation.
        'appearance-none bg-[length:1rem] bg-[right_0.75rem_center] bg-no-repeat pr-9',
        "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath d='M5.5 7.5 10 12l4.5-4.5'/%3E%3C/svg%3E\")]",
        className,
      )}
      {...rest}
    >
      {placeholder !== undefined ? (
        <option value="" disabled>
          {placeholder}
        </option>
      ) : null}
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled === true}>
          {o.label}
        </option>
      ))}
    </select>
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, rows = 4, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(CONTROL_BASE, 'px-3 py-2 text-base leading-relaxed sm:text-sm', className)}
      {...rest}
    />
  );
});
